import React, { useMemo, useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import Select from 'react-select';
import { listApi, peopleApi, raciApi, evidenceRegistersApi, linkedArtefactsApi, api } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { PageHeader } from '../components/PageHeader';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { type Tone, toneFromMap } from '../constants/tone';
import { reactSelectStyles } from '../utils/reactSelectStyles';

type RaciRole = 'R' | 'A' | 'C' | 'I';
type TypeFilter = 'both' | 'controls' | 'evidence';

const ROLE_COLOUR: Record<RaciRole, Tone> = { R: 'primary', A: 'success', C: 'info', I: 'warning' };
const ROLE_LABEL: Record<RaciRole, string> = { R: 'Responsible', A: 'Accountable', C: 'Consulted', I: 'Informed' };
const ITEM_TYPE_TONE: Record<string, Tone> = { control: 'primary', evidence: 'info' };

export const BulkRaciPage: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: allRaci = [], isLoading: raciLoading } = useQuery({ queryKey: ['raci'], queryFn: raciApi.getAll });
  const { data: rawControls = [], isLoading: ctrlLoading } = useQuery({
    queryKey: ['controls-all'],
    queryFn: listApi.controls,
  });
  const { data: rawEvidence = [], isLoading: evLoading } = useQuery({
    queryKey: ['evidence-all'],
    queryFn: listApi.evidence,
  });

  const isLoading = raciLoading || ctrlLoading || evLoading;

  // ── Filter state ──────────────────────────────────────────────────────────
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('both');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [search, setSearch] = useState('');

  // ── Selection ─────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ── Assignment form ───────────────────────────────────────────────────────
  const [assignPersonId, setAssignPersonId] = useState('');
  const [assignRole, setAssignRole] = useState<RaciRole>('R');
  const [isApplying, setIsApplying] = useState(false);

  // ── Replace-person form ───────────────────────────────────────────────────
  const [replaceFrom, setReplaceFrom] = useState('');
  const [replaceTo, setReplaceTo] = useState('');
  const [replaceRole, setReplaceRole] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);

  // ── Sync evidence RACI from controls ─────────────────────────────────────
  const [syncRegisterId, setSyncRegisterId] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const { data: evidenceRegisters = [] } = useQuery({
    queryKey: ['evidenceRegisters'],
    queryFn: evidenceRegistersApi.getAll,
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getPersonName = (personId: string): string => {
    const p = (people as any[]).find((x: any) => x.personId === personId);
    if (!p) return personId;
    const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
    return full || p.personName || personId;
  };

  // Build map: artefactId → RACI assignments
  const raciMap = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const a of allRaci as any[]) {
      const id = a.controlId || a.evidenceId || a.riskId || a.issueId || a.incidentId || a.threatId || a.remediationId || a.documentId;
      if (!id) continue;
      const list = map.get(id) || [];
      list.push(a);
      map.set(id, list);
    }
    return map;
  }, [allRaci]);

  // Normalise items to { id, name, type }
  const allItems = useMemo(() => {
    const controls = (rawControls as any[]).map((c: any) => ({
      id: c.controlId as string,
      name: (c.fields?.control_title || c.fields?.title || c.controlId) as string,
      displayId: (c.fields?.control_id || c.controlId) as string,
      type: 'control' as const,
    }));
    const evidence = (rawEvidence as any[]).map((e: any) => ({
      id: e.evidenceId as string,
      name: (e.fields?.evidence_title || e.fields?.title || e.evidenceId) as string,
      displayId: e.evidenceId as string,
      type: 'evidence' as const,
    }));

    let items = typeFilter === 'controls' ? controls : typeFilter === 'evidence' ? evidence : [...controls, ...evidence];

    if (unassignedOnly) items = items.filter((i) => !raciMap.has(i.id));
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((i) => i.name.toLowerCase().includes(q) || i.id.toLowerCase().includes(q));
    }
    return items;
  }, [rawControls, rawEvidence, typeFilter, unassignedOnly, raciMap, search]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const toggleSelectAll = (checked: boolean) =>
    setSelectedIds(checked ? new Set(allItems.map((i) => i.id)) : new Set());

  const toggleItem = (id: string, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });

  const handleApply = async () => {
    if (!assignPersonId || selectedIds.size === 0) return;
    setIsApplying(true);
    try {
      const selected = allItems.filter((i) => selectedIds.has(i.id));
      const assignments = selected.map((item) => ({
        artefactType: item.type,
        artefactId: item.id,
        personId: assignPersonId,
        role: assignRole,
      }));
      const result = (await raciApi.batchCreate(assignments)) as any;
      const { created, skipped } = result;
      toast.success(
        `Applied to ${created} item${created !== 1 ? 's' : ''}${skipped > 0 ? ` (${skipped} already had this assignment)` : ''}.`
      );
      qc.invalidateQueries({ queryKey: ['raci'] });
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to apply RACI assignments.');
    } finally {
      setIsApplying(false);
    }
  };

  const handleSyncRegister = async () => {
    if (!syncRegisterId) return;
    const selectedReg = (evidenceRegisters as any[]).find((r: any) => r.registerId === syncRegisterId);
    const regName = selectedReg?.name || syncRegisterId;
    const confirmed = await swal.confirm(
      `Sync RACI for "${regName}"?`,
      'This will copy RACI assignments from linked controls to all evidence items in this register. Existing assignments will be preserved and no data will be deleted.'
    );
    if (!confirmed) return;
    setIsSyncing(true);
    try {
      // 1. Fetch all evidence items in the register
      const evRes = await api.get<{ evidence: any[] }>(`/evidence?registerId=${encodeURIComponent(syncRegisterId)}`);
      const evidenceItems: any[] = (evRes as any).evidence || [];

      if (evidenceItems.length === 0) {
        toast.info('No evidence items found in this register.');
        setIsSyncing(false);
        return;
      }

      const batchAssignments: Array<{ artefactType: string; artefactId: string; personId: string; role: string }> = [];
      const seenKeys = new Set<string>();
      const addAssignment = (evidenceId: string, personId: string, role: string) => {
        const key = `${evidenceId}:${personId}:${role}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          batchAssignments.push({ artefactType: 'evidence', artefactId: evidenceId, personId, role });
        }
      };

      // Pass 1: evidence-centric — follow each evidence item's own linked_artefacts for controls
      await Promise.allSettled(
        evidenceItems.map(async (ev: any) => {
          const evidenceId: string = ev.evidenceId;
          if (!evidenceId) return;

          const linksData = await linkedArtefactsApi.getLinks(evidenceId, 'evidence');
          const controlLinks: any[] = (linksData?.links || []).filter((l: any) => l.artefact_type === 'control');
          if (controlLinks.length === 0) return;

          const controlRaci = await Promise.allSettled(
            controlLinks.map((l: any) => raciApi.getByArtefact('control', l.artefact_id))
          );
          for (const result of controlRaci) {
            if (result.status !== 'fulfilled') continue;
            for (const r of result.value) addAssignment(evidenceId, r.personId, r.role);
          }
        })
      );

      // Pass 2: control-centric — scan controls' own linked_artefacts for evidence in this register.
      // This catches cases where the bidirectional link wasn't written to the evidence side
      // (e.g. if evidence was linked from the control matrix page and the reverse link failed).
      const evidenceIdSet = new Set(evidenceItems.map((ev: any) => ev.evidenceId).filter(Boolean));
      const controlsWithLinkedEvidence = (rawControls as any[]).filter((ctrl: any) => {
        const la = ctrl.linked_artefacts;
        return Array.isArray(la) && la.some((link: any) =>
          String(link.artefact_type || '').toLowerCase() === 'evidence' &&
          evidenceIdSet.has(String(link.artefact_id || ''))
        );
      });

      if (controlsWithLinkedEvidence.length > 0) {
        const pass2Results = await Promise.allSettled(
          controlsWithLinkedEvidence.map(async (ctrl: any) => {
            const raci = await raciApi.getByArtefact('control', ctrl.controlId);
            const linkedEvidenceIds = (ctrl.linked_artefacts as any[])
              .filter((link: any) =>
                String(link.artefact_type || '').toLowerCase() === 'evidence' &&
                evidenceIdSet.has(String(link.artefact_id || ''))
              )
              .map((link: any) => String(link.artefact_id));
            return { raci, linkedEvidenceIds };
          })
        );
        for (const result of pass2Results) {
          if (result.status !== 'fulfilled') continue;
          const { raci, linkedEvidenceIds } = result.value;
          for (const evidenceId of linkedEvidenceIds) {
            for (const r of raci) addAssignment(evidenceId, r.personId, r.role);
          }
        }
      }

      if (batchAssignments.length === 0) {
        toast.info('No RACI found on linked controls — nothing to sync. Ensure controls have RACI assignments and are linked to evidence items.');
        setIsSyncing(false);
        return;
      }

      const result = (await raciApi.batchCreate(batchAssignments)) as any;
      const { created, skipped } = result;
      qc.invalidateQueries({ queryKey: ['raci'] });
      toast.success(
        `Synced ${created} assignment${created !== 1 ? 's' : ''} across ${evidenceItems.length} evidence items` +
        (skipped > 0 ? ` (${skipped} already existed)` : '') + '.'
      );
    } catch {
      toast.error('Failed to sync RACI from controls.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleReplace = async () => {
    if (!replaceFrom || !replaceTo || replaceFrom === replaceTo) return;
    setIsReplacing(true);
    try {
      const result = (await raciApi.replacePerson(replaceFrom, replaceTo, replaceRole || undefined)) as any;
      const { updated } = result;
      toast.success(`Updated ${updated} assignment${updated !== 1 ? 's' : ''}.`);
      qc.invalidateQueries({ queryKey: ['raci'] });
      setReplaceFrom('');
      setReplaceTo('');
      setReplaceRole('');
    } catch {
      toast.error('Failed to replace person.');
    } finally {
      setIsReplacing(false);
    }
  };

  const allSelected = allItems.length > 0 && allItems.every((i) => selectedIds.has(i.id));
  const someSelected = allItems.some((i) => selectedIds.has(i.id));

  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(allItems, 25);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-shield-user-line"
        title="Bulk RACI Assignment"
        subtitle="Assign RACI roles across multiple controls and evidence items at once."
        actions={
          <a href="/raci" className="np-btn np-btn-ghost">
            <i className="ri-arrow-left-line"></i>Back to RACI
          </a>
        }
      />

      <div className="row g-4">
        {/* ── Left column: items table ── */}
        <div className="col-12 col-xl-8">
          <div className="np-toolbar mb-3">
            {/* Filter bar */}
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <div className="np-search" style={{ maxWidth: 240 }}>
                <i className="ri-search-line"></i>
                <input
                  type="text"
                  placeholder="Search items…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="np-icon-btn" onClick={() => setSearch('')}>
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>

              <select
                className="np-select"
                style={{ maxWidth: 150 }}
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value as TypeFilter); setSelectedIds(new Set()); }}
              >
                <option value="both">Controls + Evidence</option>
                <option value="controls">Controls only</option>
                <option value="evidence">Evidence only</option>
              </select>

              <div className="form-check mb-0 ms-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="unassignedOnly"
                  checked={unassignedOnly}
                  onChange={(e) => setUnassignedOnly(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="unassignedOnly">Unassigned only</label>
              </div>
            </div>

            <small className="np-sub np-mb-0 np-tabular">
              {allItems.length} item{allItems.length !== 1 ? 's' : ''}
              {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
            </small>
          </div>

          <div className="np-card">
            <div className="np-card-body p-0">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : allItems.length === 0 ? (
                <div className="text-center py-5 np-sub">
                  <i className="ri-shield-user-line ri-3x d-block mb-2 opacity-25"></i>
                  No items match your filters.
                </div>
              ) : (
                <NimbusTable
                  thead={
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th>Name</th>
                      <th style={{ width: 90 }}>Type</th>
                      <th>Current RACI</th>
                    </tr>
                  }
                >
                  {pageItems.map((item) => {
                    const assignments = raciMap.get(item.id) || [];
                    return (
                      <tr key={item.id} className={selectedIds.has(item.id) ? 'table-active' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedIds.has(item.id)}
                            onChange={(e) => toggleItem(item.id, e.target.checked)}
                          />
                        </td>
                        <td>
                          <div className="fw-semibold small">{item.name}</div>
                          <div className="np-sub" style={{ fontSize: '0.72rem' }}>{item.displayId}</div>
                        </td>
                        <td>
                          <span className={`np-chip np-tone-${toneFromMap(ITEM_TYPE_TONE, item.type, 'info')}`}>
                            {item.type}
                          </span>
                        </td>
                        <td>
                          {assignments.length === 0 ? (
                            <span className="np-sub small">—</span>
                          ) : (
                            <div className="d-flex gap-1 flex-wrap">
                              {assignments.map((a: any) => (
                                <span
                                  key={a.raciId || `${a.personId}-${a.role}`}
                                  className={`np-chip np-tone-${toneFromMap(ROLE_COLOUR, a.role as RaciRole, 'neutral')}`}
                                  title={`${ROLE_LABEL[a.role as RaciRole] || a.role}: ${getPersonName(a.personId)}`}
                                >
                                  {a.role} · {getPersonName(a.personId)}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </NimbusTable>
              )}
              {pageItems.length > 0 && (
                <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
              )}
            </div>
          </div>
        </div>

        {/* ── Right column: action panels ── */}
        <div className="col-12 col-xl-4">

          {/* Assign panel */}
          <div className="np-card mb-4">
            <div className="np-card-head">
              <h2><i className="ri-user-add-line me-2"></i>Assign Role</h2>
            </div>
            <div className="np-card-body">
              {selectedIds.size === 0 ? (
                <p className="np-sub small mb-0">Select items from the table to assign RACI roles.</p>
              ) : (
                <>
                  <p className="small mb-3">
                    Applying to <strong>{selectedIds.size}</strong> selected item{selectedIds.size !== 1 ? 's' : ''}.
                  </p>

                  <div className="np-field">
                    <label>Person</label>
                    <Select
                      options={(people as any[]).map((p: any) => ({ value: p.personId, label: getPersonName(p.personId) }))}
                      value={(() => {
                        const opt = people.find((p) => p.personId === assignPersonId);
                        return opt ? { value: opt.personId, label: getPersonName(opt.personId) } : null;
                      })()}
                      onChange={(opt) => setAssignPersonId(opt?.value ?? '')}
                      placeholder="Select a person…"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={reactSelectStyles()}
                    />
                  </div>

                  <div className="np-field">
                    <label>Role</label>
                    <div>
                      {(['R', 'A', 'C', 'I'] as RaciRole[]).map((r) => (
                        <div key={r} className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id={`role-${r}`}
                            name="assignRole"
                            value={r}
                            checked={assignRole === r}
                            onChange={() => setAssignRole(r)}
                          />
                          <label className="form-check-label small" htmlFor={`role-${r}`}>
                            <span className={`np-chip np-tone-${toneFromMap(ROLE_COLOUR, r, 'neutral')} me-1`}>{r}</span>
                            {ROLE_LABEL[r]}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    className="np-btn np-btn-primary np-btn-sm w-100"
                    onClick={handleApply}
                    disabled={isApplying || !assignPersonId}
                  >
                    {isApplying ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span>Applying…</>
                    ) : (
                      <>Apply to {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''}</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Replace person panel */}
          <div className="np-card">
            <div className="np-card-head">
              <h2><i className="ri-user-shared-line me-2"></i>Replace Person</h2>
            </div>
            <div className="np-card-body">
              <p className="np-sub small mb-3">
                Replace one person with another across all their RACI assignments.
              </p>

              <div className="np-field">
                <label>Replace</label>
                <select
                  className="np-select"
                  value={replaceFrom}
                  onChange={(e) => setReplaceFrom(e.target.value)}
                >
                  <option value="">Select person to replace…</option>
                  {(people as any[]).map((p: any) => (
                    <option key={p.personId} value={p.personId}>{getPersonName(p.personId)}</option>
                  ))}
                </select>
              </div>

              <div className="np-field">
                <label>With</label>
                <select
                  className="np-select"
                  value={replaceTo}
                  onChange={(e) => setReplaceTo(e.target.value)}
                >
                  <option value="">Select replacement person…</option>
                  {(people as any[]).filter((p: any) => p.personId !== replaceFrom).map((p: any) => (
                    <option key={p.personId} value={p.personId}>{getPersonName(p.personId)}</option>
                  ))}
                </select>
              </div>

              <div className="np-field">
                <label>Role filter <span className="np-sub">(optional)</span></label>
                <select
                  className="np-select"
                  value={replaceRole}
                  onChange={(e) => setReplaceRole(e.target.value)}
                >
                  <option value="">All roles</option>
                  {(['R', 'A', 'C', 'I'] as RaciRole[]).map((r) => (
                    <option key={r} value={r}>{r} — {ROLE_LABEL[r]}</option>
                  ))}
                </select>
              </div>

              <button
                className="np-btn np-btn-warning np-btn-sm w-100"
                onClick={handleReplace}
                disabled={isReplacing || !replaceFrom || !replaceTo || replaceFrom === replaceTo}
              >
                {isReplacing ? (
                  <><span className="spinner-border spinner-border-sm me-1"></span>Replacing…</>
                ) : (
                  <>Replace assignments</>
                )}
              </button>
            </div>
          </div>

          {/* Sync evidence RACI from linked controls */}
          <div className="np-card mt-4">
            <div className="np-card-head">
              <h2><i className="ri-refresh-line me-2"></i>Sync Evidence RACI from Controls</h2>
            </div>
            <div className="np-card-body">
              <p className="np-sub small mb-3">
                For every evidence item in the selected register, copy RACI assignments from its linked controls. Existing assignments are preserved.
              </p>
              <div className="np-field">
                <label>Evidence Register</label>
                <select
                  className="np-select"
                  value={syncRegisterId}
                  onChange={(e) => setSyncRegisterId(e.target.value)}
                >
                  <option value="">Select register…</option>
                  {(evidenceRegisters as any[])
                    .slice()
                    .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
                    .map((r: any) => (
                      <option key={r.registerId} value={r.registerId}>{r.name}</option>
                    ))}
                </select>
              </div>
              <button
                className="np-btn np-btn-info np-btn-sm w-100"
                onClick={handleSyncRegister}
                disabled={isSyncing || !syncRegisterId}
              >
                {isSyncing ? (
                  <><span className="spinner-border spinner-border-sm me-1"></span>Syncing…</>
                ) : (
                  <><i className="ri-refresh-line me-1"></i>Sync register</>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
